/**
 * Therapeutic Areas Field Registry
 * Single source of truth for all therapeutic area field definitions
 */

import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';
import { TherapeuticArea } from '../../services/api/domains/therapeutic-areas/types';

// Type alias for convenience and type safety
type TherapeuticAreaFieldDefinition = BaseFieldDefinition<TherapeuticArea>;

/**
 * Comprehensive field registry for Therapeutic Areas
 * This is the single source of truth for all field metadata
 */
export const THERAPEUTIC_AREAS_FIELDS_REGISTRY: TherapeuticAreaFieldDefinition[] =
  [
    // ========== Core Information ==========
    {
      key: 'therapeutic_area_name',
      label: 'Therapeutic Area Name',
      dataType: 'string',
      primitiveType: 'string',
      isSortable: true,
      isFilterable: true,
      isSearchable: true,
      isEditable: true,
      isRequired: true,
      renderConfig: {
        flex: 1,
        minWidth: 200,
      },
      validation: {
        maxLength: 200,
      },
    },

    // ========== Metadata Fields ==========
    {
      key: 'user_id',
      label: 'User ID',
      dataType: 'string',
      primitiveType: 'string',
      isSortable: true,
      isFilterable: true,
      isSearchable: true,
      isEditable: false,
      isReadOnly: true,
      renderConfig: {
        width: 200,
      },
    },
    {
      key: 'created_date',
      label: 'Created Date',
      dataType: 'date',
      primitiveType: 'string',
      isSortable: true,
      isFilterable: true,
      isSearchable: true,
      isEditable: false,
      isReadOnly: true,
      renderConfig: {
        width: 180,
      },
    },
    {
      key: 'modified_date',
      label: 'Modified Date',
      dataType: 'date',
      primitiveType: 'string',
      isSortable: true,
      isFilterable: true,
      isSearchable: true,
      isEditable: false,
      isReadOnly: true,
      renderConfig: {
        width: 180,
      },
    },
  ];

// Create all helper functions at once using shared factory
const helpers = createFieldHelpers<TherapeuticArea>(
  THERAPEUTIC_AREAS_FIELDS_REGISTRY
);

// Export individual helpers for convenient importing
export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;
